import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  IconShieldCheck,
  IconLockOpen,
  IconMail,
  IconLock,
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
} from '@tabler/icons-react';

export default function PasswordReset() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    return score;
  };

  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColours = ['', 'var(--color-danger)', 'var(--color-warning)', '#FAC775', 'var(--color-success)'];

  const strength = getStrength(password);

  const handleSendReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setStep(2);
    } catch (err) {
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address');
          break;
        default:
          setError('Something went wrong — please try again');
      }
    }
    setLoading(false);
  };

  const leftContent = {
    1: {
      icon: IconLockOpen,
      title: 'Forgot your password?',
      body: 'No problem. Enter your work email and we\'ll send you a link to reset it.',
    },
    2: {
      icon: IconMail,
      title: 'Check your inbox.',
      body: 'A reset link is on its way. It expires in 30 minutes.',
    },
    3: {
      icon: IconLock,
      title: 'Almost there.',
      body: 'Choose a strong password. At least 8 characters with a mix of letters and numbers.',
    },
  };

  const left = leftContent[step];
  const LeftIcon = left.icon;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel */}
      <div style={{
        width: '40%',
        background: 'var(--color-brand)',
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>Veritas</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <LeftIcon size={26} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 400, color: '#fff', lineHeight: 1.35, marginBottom: 14 }}>
            {left.title}
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
            {left.body}
          </div>
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
        overflow: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          {/* Step 1 — Request reset */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 6 }}>Reset your password</h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.5 }}>
                Enter the email address associated with your account and we'll send you a reset link.
              </p>
              <form onSubmit={handleSendReset}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Work email address
                  </label>
                  <input
                    type="email"
                    placeholder="name@georgebrowns.co.uk"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                {error && (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--color-danger)',
                    background: 'var(--color-danger-light)',
                    borderRadius: 'var(--radius-md)',
                    padding: '10px 14px',
                    marginBottom: 16,
                  }}>
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: 'var(--color-brand)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px',
                    fontSize: 14,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    marginBottom: 16,
                  }}
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                  {!loading && <IconArrowRight size={16} stroke={1.5} />}
                </button>
              </form>
              <div
                onClick={() => navigate('/login')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}
              >
                <IconArrowLeft size={14} stroke={1.5} />
                Back to sign in
              </div>
            </>
          )}

          {/* Step 2 — Check email */}
          {step === 2 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'var(--color-brand-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}>
                <IconMail size={32} color="var(--color-brand)" stroke={1.5} />
              </div>
              <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 8 }}>Email sent</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 32, maxWidth: 360, margin: '0 auto 32px' }}>
                We've sent a reset link to{' '}
                <strong style={{ color: 'var(--color-text-primary)' }}>{email}</strong>.
                Click the link in the email to set a new password.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32, textAlign: 'left' }}>
                {[
                  { num: 1, text: <>Open the email from <strong>noreply@veritas.agriware.uk</strong></> },
                  { num: 2, text: <>Click <strong>Reset my password</strong> in the message</> },
                  { num: 3, text: 'Set a new password and sign back in' },
                ].map(s => (
                  <div key={s.num} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 14px',
                  }}>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'var(--color-brand)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                      {s.num}
                    </div>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{s.text}</span>
                  </div>
                ))}
              </div>
              <div
                onClick={handleSendReset}
                style={{ fontSize: 13, color: 'var(--color-brand)', cursor: 'pointer', marginBottom: 8 }}
              >
                Resend reset email
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
                Link expires in 30 minutes · Check your spam folder
              </div>
              <div
                onClick={() => navigate('/login')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}
              >
                <IconArrowLeft size={14} stroke={1.5} />
                Back to sign in
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}